import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeviceThermostatIcon from "@mui/icons-material/DeviceThermostat";
import VideocamIcon from "@mui/icons-material/Videocam";
import RouterIcon from "@mui/icons-material/Router";
import SpeakerIcon from "@mui/icons-material/Speaker";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import TitleBar from "../../components/navbar/TitleBar";
import NavMenu from "../../components/navbar/NavMenu";
import Footer from "../../components/common/Footer";
import SubTitleNew from "../../components/common/New/SubTitleNew";
import config from "../../config";
import {
  getDeviceMonitorAlerts,
  getDeviceHistory,
  getDeviceMonitorLatest,
  getDeviceMonitorLatestAll,
  getDeviceStreamUrl,
  getAllTrackingDevices,
  getTrackingDevicesByOrgIdIndependent,
} from "../../components/device/deviceApi";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import axios from "axios";
import "./DeviceMonitorPage.css";
import DeviceAnalyticsSection from "../../components/device/DeviceAnalyticsSection";
import { PAGE_IDS, isJsonDrivenLayoutEnabled } from "../../config/uiLayoutConfig";
import { usePageLayout } from "../../context/LayoutContext";
import {
  getLayoutSections,
  getVisibleLayoutWidgets,
  isLayoutItemVisibleForRole,
} from "../../layout/moduleLayoutUtils";
import {
  InactiveAssetsStatsIcon,
  InAlertStatsIcon,
  OnlineActiveStatsIcon,
  TotalAssetsStatsIcon,
} from "../../components/device/deviceStatsIcons";
const DEFAULT_SENSOR_WARNING_MARGIN = 5;
const MAX_SENSOR_TREND_POINTS = 5000;
const SENSOR_TREND_DURATION_OPTIONS = [
  { value: "today", label: "Today", days: 1 },
  { value: "last_3_days", label: "Last 3 Days", days: 3 },
  { value: "last_7_days", label: "Last 7 Days", days: 7 },
  { value: "last_30_days", label: "Last 30 Days", days: 30 },
];

const AssetFilterArrowIcon = (props) => (
  <svg
    {...props}
    width="14"
    height="8"
    viewBox="0 0 14 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1 1L7 7L13 1"
      stroke="#94A3B8"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");

const labelAliases = {
  VIBRATON: "VIBRATION",
  VIBRATION_SENSOR: "VIBRATION",
  HUMIDITY_SENSOR: "HUMIDITY",
  TEMPERATURE_SENSOR: "TEMPERATURE",
  MICROPHONE_SENSOR: "MICROPHONE",
  MICROPHE: "MICROPHONE",
};

const formatLabel = (value) =>
  value
    ? String(labelAliases[normalizeKey(value)] || value)
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
        .replace(/\bGps\b/g, "GPS")
        .replace(/\bRfid\b/g, "RFID")
        .replace(/\bCctv\b/g, "CCTV")
    : "Device";

const translateLabel = (t, value, fallback = "Device") => {
  const label = formatLabel(value || fallback);
  return t(label, { defaultValue: label });
};

const getCurrentUserState = () => {
  try {
    return JSON.parse(localStorage.getItem("userState") || "{}");
  } catch {
    return {};
  }
};

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const getTelemetryValueObject = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value;
  }
  return null;
};

const isGpsLikeReading = (reading = {}) => {
  const type = normalizeKey(
    reading.sensorType || reading.type || reading.deviceType,
  );
  if (type.includes("GPS") || type.includes("TRACKER")) return true;
  const telemetry = getTelemetryValueObject(reading.value);
  return Boolean(
    telemetry &&
      (telemetry.latitude !== undefined ||
        telemetry.longitude !== undefined ||
        telemetry.temperature !== undefined ||
        telemetry.humidity !== undefined),
  );
};

const hasGpsFix = (telemetry) => {
  const latitude = toFiniteNumber(telemetry?.latitude);
  const longitude = toFiniteNumber(telemetry?.longitude);
  return (
    latitude !== null &&
    longitude !== null &&
    !(latitude === 0 && longitude === 0)
  );
};

const formatTemperatureDisplay = (value) => {
  const number = toFiniteNumber(value);
  return number === null ? "--" : `${number}°C`;
};

const formatHumidityDisplay = (value) => {
  const number = toFiniteNumber(value);
  return number === null ? "--" : `${number}%`;
};

const formatBooleanDisplay = (value) => {
  if (value === true || value === "true" || value === 1 || value === "1") {
    return "On";
  }
  if (value === false || value === "false" || value === 0 || value === "0") {
    return "Off";
  }
  return "--";
};

const formatLocationDisplay = (telemetry) => {
  const latitude = toFiniteNumber(telemetry?.latitude);
  const longitude = toFiniteNumber(telemetry?.longitude);
  if (latitude === null || longitude === null) return "--";
  if (!hasGpsFix(telemetry)) return "No GPS fix";
  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
};

const formatGpsCardValue = (reading) => {
  const telemetry = getTelemetryValueObject(reading?.value);
  if (!telemetry) return null;
  return [
    formatLocationDisplay(telemetry),
    formatTemperatureDisplay(telemetry.temperature),
    formatHumidityDisplay(telemetry.humidity),
  ].join(" · ");
};

const getWarningMarginValue = (reading) => {
  const margin = toFiniteNumber(reading?.warningMargin);
  return margin !== null && margin >= 0 ? margin : DEFAULT_SENSOR_WARNING_MARGIN;
};

const isReadingInactiveByTime = (reading) => {
  const timeoutMinutes = toFiniteNumber(reading?.deviceInactiveMinutes) ?? 10;
  const recordedAt = reading?.recordedAt ? new Date(reading.recordedAt).getTime() : null;
  if (!recordedAt || Number.isNaN(recordedAt)) return false;
  return Date.now() - recordedAt > timeoutMinutes * 60 * 1000;
};

const getReadingStatus = (reading) => {
  const value = toFiniteNumber(reading.value);
  const min = toFiniteNumber(reading.minValue);
  const max = toFiniteNumber(reading.maxValue);
  const status = normalizeKey(reading.status);

  if (["INACTIVE", "OFF", "NOT_WORKING"].includes(status)) return "ALERT";
  if (isReadingInactiveByTime(reading)) return "INACTIVE";

  if (value !== null && min !== null && max !== null) {
    if (value < min || value > max) return "ALERT";
    const range = max - min;
    if (range > 0) {
      const warningDistance = getWarningMarginValue(reading);
      if (value - min <= warningDistance || max - value <= warningDistance) {
        return "WARNING";
      }
    }
  }

  if (["ACTIVE", "ON", "OK", "NORMAL", "WORKING"].includes(status)) return "NORMAL";

  return reading.recordedAt ? "NORMAL" : "INACTIVE";
};

const statusColor = {
  NORMAL: "#1AA67A",
  WARNING: "#FF1010",
  ALERT: "#9A0D0D",
  INACTIVE: "#9A0D0D",
};

const statusLabel = {
  NORMAL: "Normal",
  WARNING: "Warning",
  ALERT: "Critical High",
  INACTIVE: "Inactive",
};

const getReadingStatusLabel = (reading) => {
  if (reading.displayStatus) return reading.displayStatus;
  if (reading.health === "ALERT") {
    const value = toFiniteNumber(reading.value);
    const min = toFiniteNumber(reading.minValue);
    const max = toFiniteNumber(reading.maxValue);
    const status = normalizeKey(reading.status);
    if (["INACTIVE", "OFF", "NOT_WORKING", "DISABLED"].includes(status)) {
      return formatLabel(status);
    }
    if (value !== null && min !== null && value < min) return "Critical Low";
    if (value !== null && max !== null && value > max) return "Critical High";
    return "Critical High";
  }
  return statusLabel[reading.health] || reading.health;
};

const MONITOR_METRIC_ICONS = {
  totalAssets: TotalAssetsStatsIcon,
  onlineAssets: OnlineActiveStatsIcon,
  inactiveAssets: InactiveAssetsStatsIcon,
  alertAssets: InAlertStatsIcon,
};

const getDeviceIcon = (reading) => {
  const type = normalizeKey(reading.sensorType || reading.deviceType);
  if (type.includes("TEMPERATURE") || type.includes("HUMIDITY")) return DeviceThermostatIcon;
  if (type.includes("CAMERA") || type.includes("CCTV")) return VideocamIcon;
  if (type.includes("SPEAKER") || type.includes("MICROPHONE")) return SpeakerIcon;
  if (type.includes("GPS")) return GpsFixedIcon;
  return RouterIcon;
};

const getAssociationName = (reading) =>
  reading.vehicleName ||
  reading.stopName ||
  (reading.vehicleId ? `Vehicle ${reading.vehicleId}` : "") ||
  (reading.stopId ? `Stop ${reading.stopId}` : "") ||
  "Unassigned";

const getAssociationType = (reading) =>
  reading.vehicleId ? "Vehicle" : reading.stopId ? "Stop" : "Other";

const formatValue = (reading) => {
  const gpsValue = formatGpsCardValue(reading);
  if (gpsValue) return gpsValue;
  if (reading.value !== null && reading.value !== undefined) {
    return `${reading.value}${reading.unit || ""}`;
  }
  return reading.status || "No reading";
};

const normalizeReading = (reading) => {
  const normalized = {
    ...reading,
    id: reading.trackingDeviceId,
    type: reading.sensorType || reading.deviceType,
    associationName: getAssociationName(reading),
    associationType: getAssociationType(reading),
  };
  normalized.health = getReadingStatus(normalized);
  return normalized;
};

const getAlertRecordKey = (alert = {}) =>
  String(
    alert.id ||
      alert.deviceAlertId ||
      alert.alertId ||
      [
        alert.trackingDeviceId || alert.trackingDevice?.id,
        alert.alertType || alert.type || alert.sensorType || alert.deviceType,
        alert.vehicleId || alert.stopId || "",
      ]
        .filter(Boolean)
        .join("-"),
  );

const buildStreamAlertRecord = (payload, reading) => {
  const sourceAlert =
    payload?.alert && typeof payload.alert === "object" ? payload.alert : null;
  const hasPayloadAlert = Boolean(payload?.alert);
  const isAlertReading = ["ALERT", "WARNING"].includes(reading?.health);
  if (!hasPayloadAlert && !isAlertReading) return null;

  const now = new Date().toISOString();
  const alertType =
    sourceAlert?.alertType ||
    sourceAlert?.type ||
    reading?.sensorType ||
    reading?.type ||
    reading?.deviceType ||
    "DEVICE_ALERT";
  const severity =
    sourceAlert?.severity || (reading?.health === "ALERT" ? "CRITICAL" : "WARNING");

  return {
    ...sourceAlert,
    id:
      sourceAlert?.id ||
      sourceAlert?.deviceAlertId ||
      sourceAlert?.alertId ||
      `${reading?.trackingDeviceId || reading?.deviceId}-${alertType}`,
    trackingDeviceId: sourceAlert?.trackingDeviceId || reading?.trackingDeviceId,
    deviceId: sourceAlert?.deviceId || reading?.deviceId,
    deviceType: sourceAlert?.deviceType || reading?.deviceType,
    sensorType: sourceAlert?.sensorType || reading?.sensorType || reading?.type,
    value: sourceAlert?.value ?? reading?.value,
    status: sourceAlert?.status || reading?.status || reading?.health,
    unit: sourceAlert?.unit || reading?.unit,
    minValue: sourceAlert?.minValue ?? reading?.minValue,
    maxValue: sourceAlert?.maxValue ?? reading?.maxValue,
    organizationId: sourceAlert?.organizationId || reading?.organizationId,
    vehicleId: sourceAlert?.vehicleId || reading?.vehicleId,
    stopId: sourceAlert?.stopId || reading?.stopId,
    vehicleName: sourceAlert?.vehicleName || reading?.vehicleName,
    stopName: sourceAlert?.stopName || reading?.stopName,
    alertType,
    severity,
    message:
      sourceAlert?.message ||
      `${reading?.deviceId || "Device"} ${reading?.health === "WARNING" ? "warning" : "alert"}`,
    createdAt: sourceAlert?.createdAt || reading?.recordedAt || now,
    updatedAt: sourceAlert?.updatedAt || reading?.recordedAt || now,
    trackingDevice: {
      ...(sourceAlert?.trackingDevice || {}),
      id: sourceAlert?.trackingDevice?.id || reading?.trackingDeviceId,
      deviceId: sourceAlert?.trackingDevice?.deviceId || reading?.deviceId,
      deviceType: sourceAlert?.trackingDevice?.deviceType || reading?.deviceType,
      sensorType:
        sourceAlert?.trackingDevice?.sensorType || reading?.sensorType || reading?.type,
    },
    vehicle:
      sourceAlert?.vehicle ||
      (reading?.vehicleName ? { id: reading?.vehicleId, name: reading.vehicleName } : undefined),
    stop:
      sourceAlert?.stop ||
      (reading?.stopName ? { id: reading?.stopId, name: reading.stopName } : undefined),
  };
};

const getResponseArray = (response, nestedKey) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (nestedKey && Array.isArray(response?.[nestedKey])) return response[nestedKey];
  if (nestedKey && Array.isArray(response?.data?.[nestedKey])) {
    return response.data[nestedKey];
  }
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
};

const getDeviceOrganizationId = (device = {}) =>
  device?.organizationId ||
  device?.organization?.id ||
  device?.assignedVehicle?.organizationId ||
  device?.Vehicle?.organizationId ||
  device?.vehicle?.organizationId ||
  device?.assignedStop?.organizationId ||
  device?.Stop?.organizationId ||
  device?.stop?.organizationId ||
  "";

const getUniqueDeviceOrganizationIds = (devices = []) =>
  Array.from(
    new Set(
      devices
        .map(getDeviceOrganizationId)
        .filter(Boolean)
        .map(String),
    ),
  );

const getOrganizationRecordId = (organization = {}) =>
  organization?.id || organization?.organizationId || organization?.orgId || "";

const fetchDeviceAlertsForOrganizations = async (organizationIds = [], params = {}) => {
  const ids = Array.from(new Set(organizationIds.filter(Boolean).map(String)));
  if (!ids.length) return [];

  const results = await Promise.allSettled(
    ids.map((organizationId) =>
      getDeviceMonitorAlerts({
        organizationId,
        ...params,
      }),
    ),
  );
  const merged = new Map();

  results.forEach((result) => {
    if (result.status !== "fulfilled") return;
    getResponseArray(result.value, "alerts").forEach((alert) => {
      merged.set(getAlertRecordKey(alert), alert);
    });
  });

  return Array.from(merged.values());
};

const getStoredAccessToken = () => {
  try {
    return localStorage.getItem("authToken") || sessionStorage.getItem("authToken") || "";
  } catch {
    return "";
  }
};

const parseSseBlock = (block) => {
  const data = block
    .split(/\r?\n/)
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.replace(/^data:\s?/, ""))
    .join("\n")
    .trim();

  if (!data) return null;
  return JSON.parse(data);
};

const getReadingDeviceKey = (reading) =>
  reading?.deviceId || reading?.trackingDeviceId || reading?.id || "";

const hasLiveReading = (reading) =>
  Boolean(
    reading?.recordedAt ||
      (reading?.value !== null && reading?.value !== undefined) ||
      reading?.status,
  );

const getSensorTrendDeviceKey = (reading) =>
  String(reading?.trackingDeviceId || reading?.id || reading?.deviceId || "");

const getTodayKey = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

const getTodayBounds = (timestamp = Date.now()) => {
  const start = new Date(timestamp);
  start.setHours(0, 0, 0, 0);
  const end = new Date(timestamp);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const getSensorTrendDurationOption = (duration) =>
  SENSOR_TREND_DURATION_OPTIONS.find((option) => option.value === duration) ||
  SENSOR_TREND_DURATION_OPTIONS[0];

const getSensorTrendRangeBounds = (duration, timestamp = Date.now()) => {
  const option = getSensorTrendDurationOption(duration);
  const { start, end } = getTodayBounds(timestamp);
  start.setDate(start.getDate() - (option.days - 1));
  return { start, end };
};

const getSensorTrendRangeKey = (duration, timestamp = Date.now()) => {
  const { start, end } = getSensorTrendRangeBounds(duration, timestamp);
  return `${duration}:${getTodayKey(start)}:${getTodayKey(end)}`;
};

const formatTrendTime = (timestamp) =>
  new Date(timestamp).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

const formatTrendTick = (timestamp, duration) =>
  getSensorTrendDurationOption(duration).days === 1
    ? formatTrendTime(timestamp)
    : new Date(timestamp).toLocaleDateString([], {
        month: "short",
        day: "numeric",
      });

const formatTrendTooltipLabel = (timestamp, duration) =>
  getSensorTrendDurationOption(duration).days === 1
    ? `Time: ${formatTrendTime(timestamp)}`
    : `Time: ${new Date(timestamp).toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })}`;

const buildSensorTrendPoint = (reading, fallbackTimestamp = Date.now()) => {
  const value = toFiniteNumber(reading?.value);
  if (value === null) return null;

  const recordedAt =
    reading?.recordedAt || reading?.createdAt || reading?.updatedAt || fallbackTimestamp;
  const timestamp = new Date(recordedAt).getTime();
  if (!Number.isFinite(timestamp)) return null;

  return {
    timestamp,
    time: formatTrendTime(timestamp),
    value,
  };
};

const normalizeHistoryTrendPoints = (historyRows = [], reading = {}) => {
  const pointsByTimestamp = new Map();

  historyRows.forEach((historyRow) => {
    const point = buildSensorTrendPoint({
      ...reading,
      ...historyRow,
      value: historyRow?.value,
      unit: historyRow?.unit || reading?.unit,
      recordedAt: historyRow?.recordedAt,
    });
    if (point) pointsByTimestamp.set(point.timestamp, point);
  });

  return Array.from(pointsByTimestamp.values())
    .sort((a, b) => a.timestamp - b.timestamp)
    .slice(-MAX_SENSOR_TREND_POINTS);
};

const upsertSensorTrendPoint = (current, reading, duration) => {
  const trendKey = getSensorTrendDeviceKey(reading);
  const point = buildSensorTrendPoint(reading);
  if (!trendKey || !point) return current;

  const { start, end } = getSensorTrendRangeBounds(duration, point.timestamp);
  if (point.timestamp < start.getTime() || point.timestamp > end.getTime()) {
    return current;
  }

  const dateKey = getSensorTrendRangeKey(duration, point.timestamp);
  const existingEntry =
    current[trendKey]?.dateKey === dateKey
      ? current[trendKey]
      : { dateKey, points: [], loaded: false };
  const pointsByTimestamp = new Map(
    existingEntry.points.map((existingPoint) => [existingPoint.timestamp, existingPoint]),
  );
  pointsByTimestamp.set(point.timestamp, point);

  return {
    ...current,
    [trendKey]: {
      ...existingEntry,
      points: Array.from(pointsByTimestamp.values())
        .sort((a, b) => a.timestamp - b.timestamp)
        .slice(-MAX_SENSOR_TREND_POINTS),
    },
  };
};

const renderSensorTrendDot = ({ cx, cy, payload }, min, max) => {
  if (cx === undefined || cy === undefined) return null;

  const value = Number(payload?.value);
  const outsideThreshold =
    Number.isFinite(value) &&
    Number.isFinite(min) &&
    Number.isFinite(max) &&
    (value < min || value > max);

  return (
    <circle
      cx={cx}
      cy={cy}
      r={outsideThreshold ? 4 : 0}
      fill="#DC2626"
      stroke="#FFFFFF"
      strokeWidth={1}
    />
  );
};

const getInventoryDeviceKey = (device) =>
  device?.deviceId || device?.trackingDeviceId || device?.id || "";

const getInventoryDeviceType = (device = {}) =>
  typeof device?.deviceType === "object"
    ? device.deviceType?.name || ""
    : device?.deviceType || "";

const getAssignedVehicle = (device = {}) =>
  device?.assignedVehicle || device?.vehicle || device?.Vehicle || null;

const getAssignedStop = (device = {}) =>
  device?.assignedStop || device?.stop || device?.Stop || null;

const getSiteKey = (site = {}) => site?.id || site?.siteId || "";

const getVehicleSiteId = (vehicle = {}) =>
  vehicle?.siteId || vehicle?.site?.id || vehicle?.site?.siteId || "";

const getStopSiteId = (stop = {}) =>
  stop?.siteId || stop?.site?.id || stop?.site?.siteId || "";

const getSiteName = (site = {}) => site?.name || site?.siteName || site?.siteId || "";

const buildSiteContextMaps = ({ vehicles = [], stops = [], sites = [] }) => {
  const vehiclesById = new Map();
  const stopsById = new Map();
  const sitesById = new Map();

  vehicles.forEach((vehicle) => {
    [vehicle?.id, vehicle?.vehicleId].filter(Boolean).forEach((key) => {
      vehiclesById.set(String(key), vehicle);
    });
  });

  stops.forEach((stop) => {
    [stop?.id, stop?.stopId].filter(Boolean).forEach((key) => {
      stopsById.set(String(key), stop);
    });
  });

  sites.forEach((site) => {
    [site?.id, site?.siteId].filter(Boolean).forEach((key) => {
      sitesById.set(String(key), site);
    });
  });

  return { vehiclesById, stopsById, sitesById };
};

const enrichDeviceSiteData = (devices = [], siteContext = {}) => {
  const { vehiclesById, stopsById, sitesById } = buildSiteContextMaps(siteContext);

  return devices.map((device) => {
    const assignedVehicle =
      getAssignedVehicle(device) ||
      vehiclesById.get(String(device?.vehicleId || ""));
    const assignedStop =
      getAssignedStop(device) ||
      stopsById.get(String(device?.stopId || device?.stopCode || ""));
    const siteId =
      device?.siteId ||
      getSiteKey(device?.site) ||
      getVehicleSiteId(assignedVehicle) ||
      getStopSiteId(assignedStop) ||
      "";
    const site =
      device?.site ||
      assignedVehicle?.site ||
      assignedStop?.site ||
      sitesById.get(String(siteId || "")) ||
      null;
    const siteName = getSiteName(site) || device?.siteName || String(siteId || "");

    return {
      ...device,
      siteId,
      site,
      siteName,
      assignedVehicle: assignedVehicle
        ? { ...assignedVehicle, site: assignedVehicle.site || site, siteId: getVehicleSiteId(assignedVehicle) || siteId }
        : device?.assignedVehicle,
      assignedStop: assignedStop
        ? { ...assignedStop, site: assignedStop.site || site, siteId: getStopSiteId(assignedStop) || siteId }
        : device?.assignedStop,
    };
  });
};

const isGpsInventoryDevice = (device = {}) =>
  normalizeKey(device?.sensorType || device?.type || getInventoryDeviceType(device)).includes("GPS");

const getInventoryHealth = (device = {}) => {
  const condition = normalizeKey(device?.workingCondition || device?.status);
  if (["WORKING", "ACTIVE", "ON", "OK", "NORMAL"].includes(condition)) return "NORMAL";
  return "INACTIVE";
};

const getInventoryStatusLabel = (health) => (health === "NORMAL" ? "Active" : "Inactive");

const getInventoryAssociation = (device) => {
  const vehicle = device?.Vehicle || device?.vehicle || device?.assignedVehicle;
  const stop = device?.Stop || device?.stop || device?.assignedStop;
  const assignmentType = normalizeKey(device?.assignmentType);

  if (assignmentType === "STOP" || device?.stopId || device?.stopName || stop) {
    return {
      associationType: "Stop",
      associationName: device?.stopName || stop?.name || stop?.stopId || "Stop",
      associationId: device?.stopId || stop?.id || stop?.stopId || "",
    };
  }

  if (assignmentType === "VEHICLE" || device?.vehicleId || device?.vehicleName || vehicle) {
    return {
      associationType: "Vehicle",
      associationName:
        device?.vehicleName ||
        vehicle?.name ||
        vehicle?.VehicleNumber ||
        vehicle?.vehicleNumber ||
        "Vehicle",
      associationId: device?.vehicleId || vehicle?.id || "",
    };
  }

  return {
    associationType: "Other",
    associationName: device?.organizationName || device?.site || "Unassigned",
    associationId: "",
  };
};

const normalizeDeviceForAnalytics = (device = {}) => {
  const deviceType = getInventoryDeviceType(device);
  const association = getInventoryAssociation(device);
  return {
    ...device,
    id: getInventoryDeviceKey(device),
    name: device?.deviceId || device?.name || `Device ${getInventoryDeviceKey(device)}`,
    type: device?.sensorType || device?.type || deviceType,
    deviceType,
    sensorType: device?.sensorType,
    min: device?.min ?? device?.minValue,
    max: device?.max ?? device?.maxValue,
    minValue: device?.minValue ?? device?.min,
    maxValue: device?.maxValue ?? device?.max,
    warningMargin: device?.warningMargin,
    deviceInactiveMinutes: device?.deviceInactiveMinutes,
    value: device?.value ?? null,
    unit: device?.unit || "",
    status: device?.status || device?.workingCondition || "",
    workingCondition: device?.workingCondition,
    ...association,
  };
};

const normalizeInventoryReading = (device = {}) => {
  const normalizedDevice = normalizeDeviceForAnalytics(device);
  const health = getInventoryHealth(device);
  const normalized = {
    ...normalizedDevice,
    id: normalizedDevice.id,
    trackingDeviceId: device?.trackingDeviceId || device?.id || normalizedDevice.id,
    deviceId: device?.deviceId || normalizedDevice.name,
    status: getInventoryStatusLabel(health),
    recordedAt:
      device?.recordedAt ||
      device?.lastReadingAt ||
      device?.lastSeenAt ||
      device?.updatedAt ||
      null,
    displayStatus: getInventoryStatusLabel(health),
    health,
    isInventoryOnly: true,
    isGpsInventoryCard: isGpsInventoryDevice(device),
  };
  return normalized;
};

const mergeCardReading = (existingReading, reading) => {
  if (!existingReading) {
    return {
      ...reading,
      isInventoryOnly: false,
    };
  }

  if (existingReading.isGpsInventoryCard) {
    const mergedGps = {
      ...existingReading,
      ...reading,
      associationName: existingReading.associationName,
      associationType: existingReading.associationType,
      associationId: existingReading.associationId,
      vehicleName: existingReading.vehicleName || reading?.vehicleName,
      stopName: existingReading.stopName || reading?.stopName,
      deviceId: existingReading.deviceId || reading?.deviceId,
      trackingDeviceId:
        existingReading.trackingDeviceId || reading?.trackingDeviceId,
      value: reading?.value ?? existingReading.value,
      recordedAt: reading?.recordedAt || existingReading.recordedAt,
      displayStatus: undefined,
      isInventoryOnly: false,
      isGpsInventoryCard: true,
    };
    mergedGps.health = getReadingStatus(mergedGps);
    mergedGps.displayStatus =
      mergedGps.health === "NORMAL" ? "Active" : undefined;
    return mergedGps;
  }

  const readingStatus =
    reading?.status !== undefined && reading?.status !== null ? reading.status : "";

  const merged = {
    ...existingReading,
    value: reading?.value ?? existingReading.value,
    unit: reading?.unit || existingReading.unit,
    minValue: reading?.minValue ?? existingReading.minValue,
    maxValue: reading?.maxValue ?? existingReading.maxValue,
    min: reading?.minValue ?? existingReading.min,
    max: reading?.maxValue ?? existingReading.max,
    warningMargin: reading?.warningMargin ?? existingReading.warningMargin,
    deviceInactiveMinutes:
      reading?.deviceInactiveMinutes ?? existingReading.deviceInactiveMinutes,
    status: readingStatus,
    recordedAt: reading?.recordedAt || existingReading.recordedAt,
    historyId: reading?.historyId || existingReading.historyId,
    messageId: reading?.messageId || existingReading.messageId,
    displayStatus: undefined,
    isInventoryOnly: false,
  };
  merged.health = getReadingStatus(merged);
  return merged;
};

const mergeAnalyticsReading = (inventoryDevice, reading) => ({
  ...normalizeDeviceForAnalytics(inventoryDevice || {}),
  id: reading?.trackingDeviceId || inventoryDevice?.id || reading?.deviceId,
  name: reading?.deviceId || inventoryDevice?.deviceId || inventoryDevice?.name,
  type: reading?.type || inventoryDevice?.sensorType || inventoryDevice?.deviceType,
  deviceType: reading?.deviceType || inventoryDevice?.deviceType,
  sensorType: reading?.sensorType || inventoryDevice?.sensorType,
  value: reading?.value ?? inventoryDevice?.value ?? null,
  min: reading?.minValue ?? inventoryDevice?.minValue ?? inventoryDevice?.min,
  max: reading?.maxValue ?? inventoryDevice?.maxValue ?? inventoryDevice?.max,
  minValue: reading?.minValue ?? inventoryDevice?.minValue ?? inventoryDevice?.min,
  maxValue: reading?.maxValue ?? inventoryDevice?.maxValue ?? inventoryDevice?.max,
  warningMargin: reading?.warningMargin ?? inventoryDevice?.warningMargin,
  deviceInactiveMinutes:
    reading?.deviceInactiveMinutes ?? inventoryDevice?.deviceInactiveMinutes,
  unit: reading?.unit || inventoryDevice?.unit || "",
  status: reading?.status || inventoryDevice?.status || inventoryDevice?.workingCondition || "",
  health: reading?.health,
  associationName:
    reading?.associationName ||
    inventoryDevice?.associationName ||
    normalizeDeviceForAnalytics(inventoryDevice || {}).associationName,
  associationType:
    reading?.associationType ||
    inventoryDevice?.associationType ||
    normalizeDeviceForAnalytics(inventoryDevice || {}).associationType,
  associationId:
    reading?.vehicleId ||
    reading?.stopId ||
    inventoryDevice?.associationId ||
    inventoryDevice?.vehicleId ||
    inventoryDevice?.stopId ||
    "",
  recordedAt: reading?.recordedAt,
});

function MetricCard({ icon: Icon, label, value, color, metricKey }) {
  const isAlertMetric = metricKey === "alertAssets";

  return (
    <div className={`asset-metric-card ${isAlertMetric ? "alert-metric" : ""}`}>
      <Icon style={{ width: 22, height: 22, flexShrink: 0 }} />
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function ReadingCard({ reading, selected, onClick }) {
  const { t } = useTranslation();
  const Icon = getDeviceIcon(reading);
  const color = statusColor[reading.health] || statusColor.INACTIVE;
  const deviceLabel = reading.deviceId || reading.trackingDeviceId || "Device";
  const telemetry = getTelemetryValueObject(reading.value);
  const isGpsCard = isGpsLikeReading(reading);
  const hasMeasuredValue =
    reading.value !== null &&
    reading.value !== undefined &&
    !telemetry;
  const statusText = getReadingStatusLabel(reading);
  const translatedStatusText = t(statusText, { defaultValue: statusText });
  const gpsCardValue = isGpsCard ? formatGpsCardValue(reading) : null;

  return (
    <button
      type="button"
      className={`asset-reading-card ${reading.health.toLowerCase()} ${
        isGpsCard ? "gps-multi-sensor" : hasMeasuredValue ? "measured" : "non-sensor"
      } ${selected ? "selected" : ""}`}
      onClick={onClick}
      style={{ "--asset-status-color": color }}
    >
      <div className="asset-card-kicker">
        <span>
          <Icon sx={{ fontSize: 13 }} />
          {reading.associationName}
        </span>
        <em>{t(reading.associationType, { defaultValue: reading.associationType })}</em>
      </div>
      <div className="asset-card-title">{translateLabel(t, reading.type)}</div>
      {isGpsCard ? (
        <>
          <div className="asset-card-value gps-card-metrics">
            <span>{gpsCardValue || translatedStatusText}</span>
          </div>
          {telemetry && (
            <div className="asset-gps-metric-list">
              <span>
                {t("Location")}: {formatLocationDisplay(telemetry)}
              </span>
              <span>
                {t("Temperature")}: {formatTemperatureDisplay(telemetry.temperature)}
              </span>
              <span>
                {t("Humidity")}: {formatHumidityDisplay(telemetry.humidity)}
              </span>
            </div>
          )}
          <div className="asset-card-state">{translatedStatusText}</div>
        </>
      ) : (
        <>
          <div className="asset-card-value">
            {hasMeasuredValue ? formatValue(reading) : translatedStatusText}
          </div>
          {hasMeasuredValue && <div className="asset-card-state">{translatedStatusText}</div>}
          {hasMeasuredValue && (
            <div className="asset-card-progress">
              <i />
            </div>
          )}
        </>
      )}
      <div className="asset-card-foot">
        <span>{t(reading.associationType, { defaultValue: reading.associationType })}</span>
        <strong>{deviceLabel}</strong>
      </div>
    </button>
  );
}

function SensorTrendChart({
  reading,
  trendEntry,
  loading,
  error,
  trendDuration,
  onTrendDurationChange,
}) {
  const { t } = useTranslation();
  const currentPoint = buildSensorTrendPoint(reading);
  const points =
    trendEntry?.points?.length || !currentPoint
      ? trendEntry?.points || []
      : [currentPoint];
  const unit = reading?.unit || "";
  const min = toFiniteNumber(reading?.minValue);
  const max = toFiniteNumber(reading?.maxValue);
  const hasThreshold = min !== null && max !== null;
  const { start, end } = getSensorTrendRangeBounds(trendDuration);
  const values = points
    .map((point) => Number(point.value))
    .filter((value) => Number.isFinite(value));

  if (currentPoint === null) return null;

  const dataMax = values.length ? Math.max(...values) : currentPoint.value;
  const dataMin = values.length ? Math.min(...values) : currentPoint.value;
  const thresholdRange = hasThreshold && max > min ? max - min : Math.max(dataMax - dataMin, 1);
  const yMin = hasThreshold ? Math.min(0, min - thresholdRange * 0.15, dataMin) : Math.min(0, dataMin);
  const yMax = hasThreshold
    ? Math.max(max + thresholdRange * 0.2, dataMax + thresholdRange * 0.15, 1)
    : Math.max(dataMax + thresholdRange * 0.2, 1);
  const thresholdDotTime = end.getTime() - 10 * 60 * 1000;

  return (
    <div className="asset-trend-panel">
      <div className="asset-trend-header">
        <strong>{t("Today Live Trend")}</strong>
        <Select
          value={trendDuration}
          onChange={(event) => onTrendDurationChange(event.target.value)}
          size="small"
          className="asset-trend-range-select"
        >
          {SENSOR_TREND_DURATION_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.label)}
            </MenuItem>
          ))}
        </Select>
      </div>

      {loading && !points.length ? (
        <div className="asset-trend-state">{t("Loading history...")}</div>
      ) : (
        <div className="asset-trend-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid stroke="#D9E1EC" vertical={false} />
              <XAxis
                dataKey="timestamp"
                type="number"
                domain={[start.getTime(), end.getTime()]}
                tickFormatter={(value) => formatTrendTick(value, trendDuration)}
                tick={{ fontSize: 9, fill: "#64748B" }}
                axisLine={false}
                tickLine={false}
                minTickGap={20}
              />
              <YAxis
                tick={{ fontSize: 9, fill: "#64748B" }}
                axisLine={false}
                tickLine={false}
                domain={[Math.floor(yMin), Math.ceil(yMax)]}
                tickCount={5}
                tickFormatter={(value) => `${value}${unit}`}
              />
              <Tooltip
                formatter={(value) => [`${value}${unit}`, t("Value")]}
                labelFormatter={(label) =>
                  formatTrendTooltipLabel(label, trendDuration)
                }
              />
              {hasThreshold && (
                <ReferenceLine
                  y={min}
                  stroke="#DC2626"
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  label={{
                    value: `Min ${min}${unit}`,
                    fill: "#DC2626",
                    fontSize: 9,
                    position: "insideBottomLeft",
                  }}
                />
              )}
              {hasThreshold && (
                <ReferenceLine
                  y={max}
                  stroke="#DC2626"
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  label={{
                    value: `Max ${max}${unit}`,
                    fill: "#DC2626",
                    fontSize: 9,
                    position: "insideTopLeft",
                  }}
                />
              )}
              <Line
                type="monotone"
                dataKey="value"
                stroke={reading?.health === "ALERT" ? "#AF0000" : "#1D9E75"}
                strokeWidth={2}
                dot={(props) => renderSensorTrendDot(props, min, max)}
                activeDot={{ r: 4, fill: "#1D9E75", stroke: "#FFFFFF", strokeWidth: 1 }}
                isAnimationActive={false}
              />
              {hasThreshold && (
                <ReferenceDot
                  x={thresholdDotTime}
                  y={min}
                  r={4}
                  fill="#DC2626"
                  stroke="#FFFFFF"
                  strokeWidth={1}
                />
              )}
              {hasThreshold && (
                <ReferenceDot
                  x={thresholdDotTime}
                  y={max}
                  r={4}
                  fill="#DC2626"
                  stroke="#FFFFFF"
                  strokeWidth={1}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {hasThreshold && (
        <div className="asset-trend-thresholds">
          <span>
            {t("Min Threshold")}: {min}
            {unit}
          </span>
          <span>
            {t("Max Threshold")}: {max}
            {unit}
          </span>
        </div>
      )}
      {error && <div className="asset-trend-error">{error}</div>}
    </div>
  );
}

function DetailPanel({
  reading,
  onClose,
  trendEntry,
  trendLoading,
  trendError,
  trendDuration,
  onTrendDurationChange,
}) {
  const { t } = useTranslation();
  if (!reading) return null;

  const telemetry = getTelemetryValueObject(reading.value);
  const isGpsDetails = isGpsLikeReading(reading);
  const hasMeasuredValue =
    reading.value !== null &&
    reading.value !== undefined &&
    !telemetry;
  const hasThreshold =
    reading.minValue !== null &&
    reading.minValue !== undefined &&
    reading.maxValue !== null &&
    reading.maxValue !== undefined;
  const rows = [
    ["Tracking Device ID", reading.trackingDeviceId],
    ["Device ID", reading.deviceId],
    ["Type", translateLabel(t, reading.type)],
    [
      "Installed On",
      `${t(reading.associationType, { defaultValue: reading.associationType })} - ${
        reading.associationName
      }`,
    ],
    ["Status", t(getReadingStatusLabel(reading), { defaultValue: getReadingStatusLabel(reading) })],
    ...(isGpsDetails
      ? [
          ["Location", formatLocationDisplay(telemetry)],
          ["Latitude", telemetry ? String(telemetry.latitude ?? "--") : "--"],
          ["Longitude", telemetry ? String(telemetry.longitude ?? "--") : "--"],
          ["Speed", telemetry?.speed == null ? "--" : String(telemetry.speed)],
          ["Satellites", telemetry?.satellites == null ? "--" : String(telemetry.satellites)],
          ["Temperature", formatTemperatureDisplay(telemetry?.temperature)],
          ["Humidity", formatHumidityDisplay(telemetry?.humidity)],
          ["Ignition", formatBooleanDisplay(telemetry?.ignition)],
          ["Movement", formatBooleanDisplay(telemetry?.movement)],
        ]
      : [
          [
            "Value",
            hasMeasuredValue
              ? formatValue(reading)
              : t(formatValue(reading), { defaultValue: formatValue(reading) }),
          ],
          ...(hasMeasuredValue
            ? [
                [
                  "Threshold",
                  hasThreshold
                    ? `${reading.minValue}${reading.unit || ""} - ${reading.maxValue}${reading.unit || ""}`
                    : "-- --",
                ],
              ]
            : []),
        ]),
    ["Last Reading", reading.recordedAt ? new Date(reading.recordedAt).toLocaleString() : "-"],
  ];

  return (
    <div className="asset-detail-panel">
      <div className="asset-detail-title">
        <h3>
          {t("{{type}} Details", {
            type: translateLabel(t, reading.type),
          })}
        </h3>
        <button type="button" onClick={onClose}>X</button>
      </div>
      <Grid container spacing={1.25} sx={{ mt: 0.5 }}>
        {rows.map(([label, value]) => (
          <Grid item xs={12} sm={6} md={3} key={label}>
            <Typography sx={{ color: "#647490", fontSize: 11 }}>{t(label)}</Typography>
            <Typography
              sx={{
                color: "#1F242E",
                fontSize: 12,
                fontWeight: 800,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {value || "-"}
            </Typography>
          </Grid>
        ))}
      </Grid>
      {!isGpsDetails && (
        <SensorTrendChart
          reading={reading}
          trendEntry={trendEntry}
          loading={trendLoading}
          error={trendError}
          trendDuration={trendDuration}
          onTrendDurationChange={onTrendDurationChange}
        />
      )}
    </div>
  );
}

const getAssetCardColumnCount = () => {
  if (typeof window === "undefined") return 7;
  if (window.matchMedia("(max-width: 560px)").matches) return 1;
  if (window.matchMedia("(max-width: 900px)").matches) return 2;
  if (window.matchMedia("(max-width: 1200px)").matches) return 4;
  return 7;
};

function useAssetCardColumnCount() {
  const [columnCount, setColumnCount] = useState(getAssetCardColumnCount);

  useEffect(() => {
    const updateColumnCount = () => setColumnCount(getAssetCardColumnCount());
    updateColumnCount();
    window.addEventListener("resize", updateColumnCount);
    return () => window.removeEventListener("resize", updateColumnCount);
  }, []);

  return columnCount;
}

export default function DeviceMonitorPage() {
  const { t } = useTranslation();
  const userState = useMemo(getCurrentUserState, []);
  const currentUser = userState?.user || {};
  const currentOrganizationId =
    currentUser?.organizationId || userState?.organization?.id || "";
  const currentOrganizationName = userState?.organization?.name || "Organization";
  const roleName = String(
    currentUser?.role?.name ||
      currentUser?.role ||
      userState?.role?.name ||
      userState?.role ||
      "",
  ).toLowerCase();
  const roleId = currentUser?.roleId || userState?.roleId || currentUser?.role?.id || userState?.role?.id;
  const isSuperAdmin =
    currentUser?.email === "sales@routeeye.io" ||
    Number(roleId) === 3 ||
    roleName.includes("superadmin") ||
    roleName.includes("super admin");
  const useJsonLayout = isJsonDrivenLayoutEnabled();
  const selectedApp = localStorage.getItem("selectedApp") || "vehicleTrack";
  const devicePageId =
    selectedApp === "schoolTrack" ? PAGE_IDS.SCHOOL_DEVICES : PAGE_IDS.FLEET_DEVICES;
  const { layout: monitorLayout } = usePageLayout(
    useJsonLayout ? devicePageId : null,
  );
  const roleKey = isSuperAdmin ? "SUPER_ADMIN" : "ORG_ADMIN";
  const layoutSections = getLayoutSections(monitorLayout, null, "monitor").filter((section) =>
    isLayoutItemVisibleForRole(section, roleKey),
  );
  const cardColumnCount = useAssetCardColumnCount();

  const [organizations, setOrganizations] = useState([]);
  const [organizationId, setOrganizationId] = useState(isSuperAdmin ? "" : currentOrganizationId);
  const [rawDeviceInventory, setDeviceInventory] = useState([]);
  const [siteContext, setSiteContext] = useState({ vehicles: [], stops: [], sites: [] });
  const deviceInventory = useMemo(
    () => enrichDeviceSiteData(rawDeviceInventory, siteContext),
    [rawDeviceInventory, siteContext],
  );
  const [readings, setReadings] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("");
  const [attachedToFilter, setAttachedToFilter] = useState("");
  const [installedAtFilter, setInstalledAtFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [siteFilter, setSiteFilter] = useState("");
  const [sortFilter, setSortFilter] = useState("STATUS_ALERT_NORMAL");
  const [selectedReadingId, setSelectedReadingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [, setStreamConnected] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [sensorTrendByDevice, setSensorTrendByDevice] = useState({});
  const [sensorTrendLoadingKey, setSensorTrendLoadingKey] = useState("");
  const [sensorTrendErrors, setSensorTrendErrors] = useState({});
  const [selectedTrendDuration, setSelectedTrendDuration] = useState("today");
  const reconnectTimeoutRef = useRef(null);
  const alertRefreshTimeoutRef = useRef(null);
  const [stops, setStops] = useState([]);

  useEffect(() => {
    if (!isSuperAdmin) return;

    const fetchOrganizations = async () => {
      try {
        const response = await axios.get(`${config.baseURL}/organization`, {
          withCredentials: true,
        });
        setOrganizations(response?.data?.organizations || []);
      } catch (err) {
        console.warn("Failed to load organizations for device monitor:", err);
      }
    };

    fetchOrganizations();
  }, [isSuperAdmin]);

  useEffect(() => {
    const allOrganizationIds = organizations.map(getOrganizationRecordId).filter(Boolean).map(String);
    const inventoryOrganizationIds = getUniqueDeviceOrganizationIds(rawDeviceInventory);
    const scopedOrganizationIds = organizationId
      ? [String(organizationId)]
      : isSuperAdmin
        ? allOrganizationIds.length
          ? allOrganizationIds
          : inventoryOrganizationIds
        : [];

    if (!scopedOrganizationIds.length) {
      setStops([]);
      setSiteContext({ vehicles: [], stops: [], sites: [] });
      return;
    }

    let isCancelled = false;

    const fetchSiteContext = async () => {
      try {
        const contextResults = await Promise.allSettled(
          scopedOrganizationIds.map(async (scopedOrganizationId) => {
            const [vehiclesResponse, stopsResponse, sitesResponse] = await Promise.all([
              axios.get(
                `${config.baseURL}/vehicle/organization/independent/${scopedOrganizationId}`,
                { withCredentials: true },
              ),
              axios.get(
                `${config.baseURL}/stop/organization/independent/${scopedOrganizationId}`,
                { withCredentials: true },
              ),
              axios.get(`${config.baseURL}/site/organization`, {
                params: { organizationId: scopedOrganizationId },
                withCredentials: true,
              }),
            ]);

            return {
              vehicles: getResponseArray(vehiclesResponse, "vehicles"),
              stops: getResponseArray(stopsResponse, "stops"),
              sites: getResponseArray(sitesResponse, "sites"),
            };
          }),
        );
        const contexts = contextResults
          .filter((result) => result.status === "fulfilled")
          .map((result) => result.value);
        if (isCancelled) return;
        const nextStops = contexts.flatMap((context) => context.stops);
        setStops(nextStops);
        setSiteContext({
          vehicles: contexts.flatMap((context) => context.vehicles),
          stops: nextStops,
          sites: contexts.flatMap((context) => context.sites),
        });
      } catch (err) {
        if (isCancelled) return;
        console.warn("Failed to load site context for device analytics:", err);
        setStops([]);
        setSiteContext({ vehicles: [], stops: [], sites: [] });
      }
    };

    fetchSiteContext();
    return () => {
      isCancelled = true;
    };
  }, [isSuperAdmin, organizationId, organizations, rawDeviceInventory]);

  const fetchMonitorData = useCallback(async () => {
    if (!organizationId && !isSuperAdmin) {
      setDeviceInventory([]);
      setReadings([]);
      setAlerts([]);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const devicesResponse = await (isSuperAdmin
        ? getAllTrackingDevices()
        : getTrackingDevicesByOrgIdIndependent(organizationId));
      const deviceData = getResponseArray(devicesResponse);
      const scopedDeviceData =
        isSuperAdmin && organizationId
          ? deviceData.filter(
              (device) =>
                String(device?.organizationId || device?.organization?.id || "") ===
                String(organizationId),
            )
          : deviceData;
      setDeviceInventory(scopedDeviceData);

      if (isSuperAdmin) {
        const [latestResponse, alertsResponse] = await Promise.all([
          getDeviceMonitorLatestAll(),
          organizationId
            ? getDeviceMonitorAlerts({ organizationId, limit: 50 })
            : fetchDeviceAlertsForOrganizations(
                getUniqueDeviceOrganizationIds(scopedDeviceData),
                { limit: 500 },
              ),
        ]);
        const latestData = getResponseArray(latestResponse);
        const scopedLatestData = organizationId
          ? latestData.filter(
              (reading) =>
                String(reading?.organizationId || reading?.organization?.id || "") ===
                String(organizationId),
            )
          : latestData;
        const alertData = getResponseArray(alertsResponse, "alerts");

        setReadings(scopedLatestData.map(normalizeReading));
        setAlerts(alertData);
        setLastUpdatedAt(new Date());
        return;
      }

      if (!organizationId) {
        setReadings([]);
        setAlerts([]);
        setLastUpdatedAt(new Date());
        return;
      }

      const monitorParams = { organizationId };
      const [latestResponse, alertsResponse] = await Promise.all([
        getDeviceMonitorLatest(monitorParams),
        getDeviceMonitorAlerts({ ...monitorParams, limit: 50 }),
      ]);
      const latestData = getResponseArray(latestResponse);
      const alertData = getResponseArray(alertsResponse, "alerts");
      setReadings(latestData.map(normalizeReading));
      setAlerts(alertData);
      setLastUpdatedAt(new Date());
    } catch (err) {
      setError(err?.response?.data?.msg || err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [organizationId, isSuperAdmin]);

  useEffect(() => {
    fetchMonitorData();
  }, [fetchMonitorData]);

  useEffect(() => {
    if (isSuperAdmin) {
      setStreamConnected(false);
      return undefined;
    }

    if (!organizationId) return undefined;

    let isCancelled = false;
    let abortController = null;

    const upsertStreamAlert = (alertRecord) => {
      if (!alertRecord) return;
      const nextKey = getAlertRecordKey(alertRecord);
      setAlerts((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        const nextType = String(
          alertRecord.alertType || alertRecord.type || alertRecord.sensorType || "",
        );
        const existingIndex = list.findIndex((item) => {
          const sameKey = getAlertRecordKey(item) === nextKey;
          const sameDeviceAndType =
            alertRecord.trackingDeviceId &&
            String(item?.trackingDeviceId || item?.trackingDevice?.id || "") ===
              String(alertRecord.trackingDeviceId) &&
            String(item?.alertType || item?.type || item?.sensorType || "") === nextType;
          return sameKey || sameDeviceAndType;
        });

        if (existingIndex === -1) return [alertRecord, ...list].slice(0, 50);

        const copy = [...list];
        copy[existingIndex] = { ...copy[existingIndex], ...alertRecord };
        return copy;
      });
    };

    const scheduleAlertRefresh = () => {
      if (alertRefreshTimeoutRef.current) return;
      alertRefreshTimeoutRef.current = setTimeout(() => {
        alertRefreshTimeoutRef.current = null;
        fetchMonitorData();
      }, 500);
    };

    const handleStreamPayload = (payload) => {
      if (!payload) return;
      try {
        if (payload.type === "connected" || payload.type === "heartbeat") return;

        const nextReading = normalizeReading({
          trackingDeviceId: payload.trackingDeviceId,
          deviceId: payload.deviceExternalId || payload.deviceId,
          deviceType: payload.deviceType,
          sensorType: payload.sensorType,
          value: payload.value,
          status: payload.status,
          unit: payload.unit,
          minValue: payload.minValue,
          maxValue: payload.maxValue,
          warningMargin: payload.warningMargin,
          deviceInactiveMinutes: payload.deviceInactiveMinutes,
          recordedAt: payload.recordedAt,
          organizationId: payload.organizationId,
          vehicleId: payload.vehicleId,
          stopId: payload.stopId,
          vehicleName: payload.vehicleName,
          stopName: payload.stopName,
        });

        setReadings((prev) => {
          const existingIndex = prev.findIndex(
            (item) => String(item.trackingDeviceId) === String(nextReading.trackingDeviceId),
          );
          if (existingIndex === -1) return [nextReading, ...prev];
          const copy = [...prev];
          copy[existingIndex] = { ...copy[existingIndex], ...nextReading };
          return copy;
        });
        setSensorTrendByDevice((current) => upsertSensorTrendPoint(current, nextReading));
        setLastUpdatedAt(new Date());

        const streamAlertRecord = buildStreamAlertRecord(payload, nextReading);
        if (streamAlertRecord) {
          upsertStreamAlert(streamAlertRecord);
          scheduleAlertRefresh();
        }
      } catch (err) {
        console.warn("Failed to parse device stream event:", err);
      }
    };

    const scheduleReconnect = () => {
      if (isCancelled || reconnectTimeoutRef.current) return;
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        connectStream();
      }, 5000);
    };

    const connectStream = async () => {
      abortController = new AbortController();
      const accessToken = getStoredAccessToken();

      try {
        const response = await fetch(getDeviceStreamUrl({ organizationId }), {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
          credentials: "include",
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Device stream failed with status ${response.status}`);
        }

        setStreamConnected(true);

        if (!response.body?.getReader) {
          throw new Error("Device stream is not readable in this browser");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (!isCancelled) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const blocks = buffer.split(/\r?\n\r?\n/);
          buffer = blocks.pop() || "";

          blocks.forEach((block) => {
            const payload = parseSseBlock(block);
            handleStreamPayload(payload);
          });
        }

        if (!isCancelled) {
          setStreamConnected(false);
          scheduleReconnect();
        }
      } catch (err) {
        if (isCancelled || err.name === "AbortError") return;
        setStreamConnected(false);
        console.warn("Device stream connection failed:", err);
        scheduleReconnect();
      }
    };

    connectStream();

    return () => {
      isCancelled = true;
      abortController?.abort();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (alertRefreshTimeoutRef.current) {
        clearTimeout(alertRefreshTimeoutRef.current);
        alertRefreshTimeoutRef.current = null;
      }
      setStreamConnected(false);
    };
  }, [organizationId, fetchMonitorData, isSuperAdmin]);

  const liveReadings = useMemo(() => {
    const mergedByKey = new Map();
    const keyLookup = new Map();

    const rememberKeys = (keys, primaryKey) => {
      keys
        .filter(Boolean)
        .map(String)
        .forEach((key) => keyLookup.set(key, primaryKey));
    };

    deviceInventory.forEach((device) => {
      const primaryKey = String(
        getInventoryDeviceKey(device) || device?.deviceId || mergedByKey.size,
      );
      mergedByKey.set(primaryKey, normalizeInventoryReading(device));
      rememberKeys([device?.deviceId, device?.trackingDeviceId, device?.id], primaryKey);
    });

    readings.forEach((reading) => {
      const readingKeys = [reading?.deviceId, reading?.trackingDeviceId, reading?.id];
      const primaryKey =
        readingKeys.map((key) => keyLookup.get(String(key || ""))).find(Boolean) ||
        String(getReadingDeviceKey(reading) || mergedByKey.size);
      const existingReading = mergedByKey.get(primaryKey);

      mergedByKey.set(primaryKey, mergeCardReading(existingReading, reading));
      rememberKeys(readingKeys, primaryKey);
    });

    return Array.from(mergedByKey.values());
  }, [deviceInventory, readings]);

  useEffect(() => {
    if (!liveReadings.length) return;
    setSensorTrendByDevice((current) =>
      liveReadings.reduce(
        (nextTrendByDevice, reading) =>
          upsertSensorTrendPoint(
            nextTrendByDevice,
            reading,
            selectedTrendDuration,
          ),
        current,
      ),
    );
  }, [liveReadings, selectedTrendDuration]);

  const selectedTrendReading = useMemo(
    () =>
      liveReadings.find(
        (reading) => String(reading.trackingDeviceId) === String(selectedReadingId),
      ) || null,
    [liveReadings, selectedReadingId],
  );

  useEffect(() => {
    const trendKey = getSensorTrendDeviceKey(selectedTrendReading);
    const currentPoint = buildSensorTrendPoint(selectedTrendReading);
    if (!trendKey || !currentPoint) return;

    const dateKey = getSensorTrendRangeKey(selectedTrendDuration);
    if (
      sensorTrendByDevice[trendKey]?.dateKey === dateKey &&
      sensorTrendByDevice[trendKey]?.loaded
    ) {
      return;
    }

    const { start, end } = getSensorTrendRangeBounds(selectedTrendDuration);
    let isCancelled = false;
    const loadingKey = `${trendKey}:${dateKey}`;

    const loadSensorHistory = async () => {
      setSensorTrendLoadingKey(loadingKey);
      setSensorTrendErrors((current) => {
        const next = { ...current };
        delete next[trendKey];
        return next;
      });

      try {
        const response = await getDeviceHistory(selectedTrendReading.trackingDeviceId, {
          deviceType: selectedTrendReading.deviceType,
          sensorType: selectedTrendReading.sensorType || selectedTrendReading.type,
          from: start.toISOString(),
          to: end.toISOString(),
          limit: MAX_SENSOR_TREND_POINTS,
        });

        if (isCancelled) return;

        const historyPoints = normalizeHistoryTrendPoints(
          getResponseArray(response),
          selectedTrendReading,
        );
        const latestPoint = buildSensorTrendPoint(selectedTrendReading);
        const pointsByTimestamp = new Map(
          historyPoints.map((point) => [point.timestamp, point]),
        );
        if (latestPoint) pointsByTimestamp.set(latestPoint.timestamp, latestPoint);

        setSensorTrendByDevice((current) => ({
          ...current,
          [trendKey]: {
            dateKey,
            loaded: true,
            points: Array.from(pointsByTimestamp.values())
              .sort((a, b) => a.timestamp - b.timestamp)
              .slice(-MAX_SENSOR_TREND_POINTS),
          },
        }));
      } catch (err) {
        if (isCancelled) return;
        setSensorTrendErrors((current) => ({
          ...current,
          [trendKey]:
            err?.response?.data?.msg ||
            err?.response?.data?.error ||
            "Unable to load sensor history",
        }));
        setSensorTrendByDevice((current) => ({
          ...current,
          [trendKey]: {
            ...(current[trendKey] || { points: [] }),
            dateKey,
            loaded: true,
          },
        }));
      } finally {
        if (!isCancelled) {
          setSensorTrendLoadingKey((current) =>
            current === loadingKey ? "" : current,
          );
        }
      }
    };

    loadSensorHistory();

    return () => {
      isCancelled = true;
    };
  }, [selectedTrendReading, selectedTrendDuration, sensorTrendByDevice]);

  const typeOptions = useMemo(() => {
    const values = new Map();
    liveReadings.forEach((reading) => {
      const key = normalizeKey(reading.type);
      if (key) values.set(key, formatLabel(reading.type));
    });
    return Array.from(values, ([value, label]) => ({ value, label })).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [liveReadings]);

  const inventoryByKey = useMemo(() => {
    const values = new Map();
    deviceInventory.forEach((device) => {
      [
        getInventoryDeviceKey(device),
        device?.trackingDeviceId,
        device?.deviceId,
      ]
        .filter(Boolean)
        .map(String)
        .forEach((key) => {
          if (!values.has(key)) values.set(key, device);
        });
    });
    return values;
  }, [deviceInventory]);

  const getInventoryForReading = useCallback(
    (reading) =>
      inventoryByKey.get(String(reading?.trackingDeviceId || "")) ||
      inventoryByKey.get(String(reading?.deviceId || "")) ||
      {},
    [inventoryByKey],
  );

  const attachedToOptions = useMemo(
    () => ["Vehicle", "Stop", "Other"].filter((option) =>
      liveReadings.some((reading) => reading.associationType === option),
    ),
    [liveReadings],
  );

  const installedAtOptions = useMemo(() => {
    const values = new Set();
    liveReadings.forEach((reading) => {
      if (reading.associationName) values.add(reading.associationName);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [liveReadings]);

  const locationOptions = useMemo(() => {
    const values = new Set();
    deviceInventory.forEach((device) => {
      [
        device?.location,
        device?.city,
        device?.organization?.city,
        device?.organization?.location,
        device?.site?.city,
        device?.site?.location,
        device?.stop?.city,
        device?.assignedStop?.city,
      ]
        .filter(Boolean)
        .forEach((value) => values.add(String(value)));
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [deviceInventory]);

  const siteOptions = useMemo(() => {
    const values = new Set();
    stops.forEach((stop) => {
      if (stop?.site?.name) values.add(stop.site.name);
      if (stop?.siteName) values.add(stop.siteName);
      if (stop?.name) values.add(stop.name);
    });
    deviceInventory.forEach((device) => {
      [
        device?.site?.name,
        device?.siteName,
        device?.stop?.site?.name,
        device?.assignedStop?.site?.name,
      ]
        .filter(Boolean)
        .forEach((value) => values.add(String(value)));
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [deviceInventory, stops]);

  const handleOrganizationFilterChange = (value) => {
    setOrganizationId(value);
    setLocationFilter("");
    setSiteFilter("");
    setInstalledAtFilter("");
  };

  const filteredReadings = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return liveReadings
      .filter((reading) => {
        const inventoryDevice = getInventoryForReading(reading);
        const locationValue =
          inventoryDevice?.location ||
          inventoryDevice?.city ||
          inventoryDevice?.organization?.city ||
          inventoryDevice?.organization?.location ||
          inventoryDevice?.site?.city ||
          inventoryDevice?.site?.location ||
          inventoryDevice?.stop?.city ||
          inventoryDevice?.assignedStop?.city ||
          "";
        const siteValue =
          inventoryDevice?.site?.name ||
          inventoryDevice?.siteName ||
          inventoryDevice?.stop?.site?.name ||
          inventoryDevice?.assignedStop?.site?.name ||
          inventoryDevice?.stop?.name ||
          inventoryDevice?.assignedStop?.name ||
          reading.associationName ||
          "";

        if (
          statusFilter !== "ALL" &&
          reading.health !== statusFilter &&
          !(statusFilter === "ALERT" && reading.health === "INACTIVE")
        ) {
          return false;
        }
        if (typeFilter && normalizeKey(reading.type) !== typeFilter) return false;
        if (attachedToFilter && reading.associationType !== attachedToFilter) return false;
        if (installedAtFilter && reading.associationName !== installedAtFilter) return false;
        if (locationFilter && String(locationValue) !== String(locationFilter)) return false;
        if (siteFilter && String(siteValue) !== String(siteFilter)) return false;
        if (!normalizedQuery) return true;
        return [
          reading.deviceId,
          reading.trackingDeviceId,
          reading.type,
          reading.associationName,
          reading.associationType,
          reading.status,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedQuery));
      })
      .sort((a, b) => {
        const severity = { ALERT: 0, INACTIVE: 0, WARNING: 1, NORMAL: 2 };
        if (sortFilter === "STATUS_NORMAL_ALERT") {
          const diff = (severity[b.health] ?? 4) - (severity[a.health] ?? 4);
          if (diff !== 0) return diff;
        } else if (sortFilter === "STATUS_ALERT_NORMAL") {
          const diff = (severity[a.health] ?? 4) - (severity[b.health] ?? 4);
          if (diff !== 0) return diff;
        } else if (sortFilter === "NAME_ASC") {
          return String(a.deviceId || a.name || "").localeCompare(String(b.deviceId || b.name || ""));
        }
        return new Date(b.recordedAt || 0) - new Date(a.recordedAt || 0);
      });
  }, [
    liveReadings,
    query,
    statusFilter,
    typeFilter,
    attachedToFilter,
    installedAtFilter,
    locationFilter,
    siteFilter,
    sortFilter,
    getInventoryForReading,
  ]);

  const metrics = useMemo(() => {
    const counts = liveReadings.reduce(
      (acc, reading) => {
        acc[reading.health] = (acc[reading.health] || 0) + 1;
        return acc;
      },
      { NORMAL: 0, WARNING: 0, ALERT: 0, INACTIVE: 0 },
    );
    return counts;
  }, [liveReadings]);

  const totalAssets = deviceInventory.length;
  const inventoryOnlineAssets = deviceInventory.filter(
    (device) => normalizeKey(device?.workingCondition) === "WORKING",
  ).length;
  const inventoryInactiveAssets = deviceInventory.filter((device) =>
    ["NOT_WORKING", "NEEDS_REPAIR"].includes(normalizeKey(device?.workingCondition)),
  ).length;
  const onlineReadingIds = new Set(
    liveReadings
      .filter((reading) => reading.health !== "INACTIVE" && hasLiveReading(reading))
      .map(getReadingDeviceKey)
      .filter(Boolean)
      .map(String),
  );
  const onlineAssets = onlineReadingIds.size || inventoryOnlineAssets;
  const alertAssets = Math.max(
    alerts.length,
    (metrics.WARNING || 0) + (metrics.ALERT || 0),
  );
  const inactiveAssets = liveReadings.length
    ? Math.max(totalAssets - onlineAssets, metrics.INACTIVE || 0, 0)
    : inventoryInactiveAssets;

  const analyticsDevices = useMemo(() => {
    const inventoryByKey = new Map();
    const keyLookup = new Map();

    const rememberKeys = (keys, primaryKey) => {
      keys
        .filter(Boolean)
        .map(String)
        .forEach((key) => keyLookup.set(key, primaryKey));
    };

    deviceInventory.forEach((device) => {
      const keys = [
        getInventoryDeviceKey(device),
        device?.trackingDeviceId,
        device?.deviceId,
        device?.id,
      ]
        .filter(Boolean)
        .map(String);

      keys.forEach((key) => {
        if (!inventoryByKey.has(key)) {
          inventoryByKey.set(key, device);
        }
      });
    });

    const mergedByKey = new Map();
    deviceInventory.forEach((device) => {
      const key = String(getInventoryDeviceKey(device) || device?.deviceId || mergedByKey.size);
      mergedByKey.set(key, normalizeDeviceForAnalytics(device));
      rememberKeys([device?.deviceId, device?.trackingDeviceId, device?.id], key);
    });

    readings.forEach((reading) => {
      const inventoryDevice =
        inventoryByKey.get(String(reading?.trackingDeviceId || "")) ||
        inventoryByKey.get(String(reading?.id || "")) ||
        inventoryByKey.get(String(reading?.deviceId || ""));
      const readingKeys = [reading?.deviceId, reading?.trackingDeviceId, reading?.id];
      const key =
        readingKeys.map((readingKey) => keyLookup.get(String(readingKey || ""))).find(Boolean) ||
        String(
          reading?.trackingDeviceId ||
            inventoryDevice?.id ||
            reading?.deviceId ||
            mergedByKey.size,
        );
      mergedByKey.set(key, mergeAnalyticsReading(inventoryDevice, reading));
      rememberKeys(readingKeys, key);
    });

    return Array.from(mergedByKey.values());
  }, [deviceInventory, readings]);
  const analyticsOrganizationIds = useMemo(
    () => getUniqueDeviceOrganizationIds(deviceInventory),
    [deviceInventory],
  );

  const selectedOrganizationName =
    (isSuperAdmin && !organizationId
      ? t("Organizations")
      : organizations.find((org) => String(org.id) === String(organizationId))?.name) ||
    currentOrganizationName;
  const titleSection = layoutSections.find((section) => section.type === "pageTitle");
  const pageTitle = titleSection?.props?.labelKey || "Live Device Monitoring";
  const monitorSections = layoutSections.length
    ? layoutSections.filter((section) => section.type !== "pageTitle")
    : useJsonLayout
      ? []
      : [
          {
            id: "metrics",
            type: "liveSummaryStats",
            props: { labelKey: "Overall Metrics" },
            widgets: [
              {
                id: "totalAssets",
                labelKey: "Total Devices",
                metricKey: "totalAssets",
                color: "#1D9E75",
              },
              {
                id: "onlineAssets",
                labelKey: "Online/Active",
                metricKey: "onlineAssets",
                color: "#1D9E75",
              },
              {
                id: "inactiveAssets",
                labelKey: "Inactive",
                metricKey: "inactiveAssets",
                color: "#1F2937",
              },
              {
                id: "alertAssets",
                labelKey: "In Alert",
                metricKey: "alertAssets",
                color: "#EF4444",
              },
            ],
          },
          {
            id: "liveData",
            type: "liveDeviceGrid",
            props: {
              labelKey: "Live Data",
              emptyMessageKey: "No live device readings found",
            },
          },
          { id: "analytics", type: "analyticsGrid" },
        ];
  const monitorMetricValues = {
    totalAssets,
    onlineAssets,
    inactiveAssets,
    alertAssets,
  };

  const renderLiveFilterWidget = (widget) => {
    switch (widget.filterKey) {
      case "search":
        return (
          <TextField
            className="asset-search-box"
            key={widget.id}
            placeholder={t(widget.labelKey || "Search")}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            size="small"
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ fontSize: 16 }} />
                </InputAdornment>
              ),
            }}
          />
        );
      case "status":
        return (
          <Select
            className="asset-filter-select"
            key={widget.id}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            size="small"
            displayEmpty
            IconComponent={AssetFilterArrowIcon}
          >
            <MenuItem value="ALL">{t(widget.labelKey || "All")}</MenuItem>
            <MenuItem value="ALERT">{t("Alert")}</MenuItem>
            <MenuItem value="WARNING">{t("Warning")}</MenuItem>
            <MenuItem value="NORMAL">{t("Normal")}</MenuItem>
            <MenuItem value="INACTIVE">{t("Inactive")}</MenuItem>
          </Select>
        );
      case "deviceType":
        return (
          <Select
            className="asset-filter-select"
            key={widget.id}
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value)}
            size="small"
            displayEmpty
            IconComponent={AssetFilterArrowIcon}
          >
            <MenuItem value="">{t(widget.labelKey || "All Device Types")}</MenuItem>
            {typeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.label)}
              </MenuItem>
            ))}
          </Select>
        );
      case "attachedTo":
        return (
          <Select
            className="asset-filter-select"
            key={widget.id}
            value={attachedToFilter}
            onChange={(event) => setAttachedToFilter(event.target.value)}
            size="small"
            displayEmpty
            IconComponent={AssetFilterArrowIcon}
          >
            <MenuItem value="">{t(widget.labelKey || "Attached to: All")}</MenuItem>
            {attachedToOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {t("Attached to: {{type}}", {
                  type: t(option, { defaultValue: option }),
                })}
              </MenuItem>
            ))}
          </Select>
        );
      case "installedAt":
        return (
          <Select
            className="asset-filter-select"
            key={widget.id}
            value={installedAtFilter}
            onChange={(event) => setInstalledAtFilter(event.target.value)}
            size="small"
            displayEmpty
            IconComponent={AssetFilterArrowIcon}
          >
            <MenuItem value="">{t(widget.labelKey || "Installed At")}</MenuItem>
            {installedAtOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        );
      case "location":
        if (!isSuperAdmin) return null;
        return (
          <Select
            className="asset-filter-select"
            key={widget.id}
            value={locationFilter}
            onChange={(event) => setLocationFilter(event.target.value)}
            size="small"
            displayEmpty
            IconComponent={AssetFilterArrowIcon}
          >
            <MenuItem value="">{t(widget.labelKey || "Locations")}</MenuItem>
            {locationOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        );
      case "organization":
        if (!isSuperAdmin) return null;
        return (
          <Select
            className="asset-filter-select"
            key={widget.id}
            value={organizationId}
            onChange={(event) => handleOrganizationFilterChange(event.target.value)}
            size="small"
            displayEmpty
            IconComponent={AssetFilterArrowIcon}
          >
            <MenuItem value="">{t(widget.labelKey || "Organizations")}</MenuItem>
            {organizations.map((organization) => (
              <MenuItem key={organization.id} value={organization.id}>
                {organization.name}
              </MenuItem>
            ))}
          </Select>
        );
      case "site":
        return (
          <Select
            className="asset-filter-select"
            key={widget.id}
            value={siteFilter}
            onChange={(event) => setSiteFilter(event.target.value)}
            size="small"
            displayEmpty
            IconComponent={AssetFilterArrowIcon}
          >
            <MenuItem value="">{t(widget.labelKey || "Sites")}</MenuItem>
            {siteOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        );
      case "sort":
        return (
          <label className="asset-sort-select" key={widget.id}>
            <span>{t(widget.labelKey || "Sort By:")}</span>
            <Select
              className="asset-filter-select asset-sort-filter-select"
              value={sortFilter}
              onChange={(event) => setSortFilter(event.target.value)}
              size="small"
              IconComponent={AssetFilterArrowIcon}
            >
              <MenuItem value="STATUS_ALERT_NORMAL">{t("Status: Alert To Normal")}</MenuItem>
              <MenuItem value="STATUS_NORMAL_ALERT">{t("Status: Normal To Alert")}</MenuItem>
              <MenuItem value="LATEST">{t("Latest Updated")}</MenuItem>
              <MenuItem value="NAME_ASC">{t("Device Name")}</MenuItem>
            </Select>
          </label>
        );
      default:
        return null;
    }
  };

  const renderMetricsSection = (section) => {
    const widgets = getVisibleLayoutWidgets(section, roleKey);
    const metricWidgets = useJsonLayout
      ? widgets
      : widgets.length
        ? widgets
        : [
          { id: "totalAssets", labelKey: "Total Devices", metricKey: "totalAssets", color: "#1D9E75" },
          { id: "onlineAssets", labelKey: "Online / Active", metricKey: "onlineAssets", color: "#1D9E75" },
          { id: "inactiveAssets", labelKey: "Inactive", metricKey: "inactiveAssets", color: "#1F2937" },
          { id: "alertAssets", labelKey: "In Alert", metricKey: "alertAssets", color: "#EF4444" },
        ];

    if (metricWidgets.length === 0) return null;

    return (
      <section className="asset-metrics-section" key={section.id}>
        <span>{t(section?.props?.labelKey || "Overall Metrics")}</span>
        <div className="asset-metrics-grid">
          {metricWidgets.map((widget) => {
            const Icon = MONITOR_METRIC_ICONS[widget.metricKey] || TotalAssetsStatsIcon;
            return (
              <MetricCard
                key={widget.id || widget.metricKey}
                icon={Icon}
                label={t(widget.labelKey)}
                value={monitorMetricValues[widget.metricKey] ?? 0}
                color={widget.color || "#1D9E75"}
                metricKey={widget.metricKey}
              />
            );
          })}
        </div>
      </section>
    );
  };

  const renderLiveDataSection = (section) => {
    const filterWidgets = getVisibleLayoutWidgets(section, roleKey);
    const visibleFilterWidgets = useJsonLayout
      ? filterWidgets
      : filterWidgets.length
        ? filterWidgets
        : [
          { id: "search", filterKey: "search", labelKey: "Search" },
          { id: "status", filterKey: "status", labelKey: "All" },
          { id: "deviceType", filterKey: "deviceType", labelKey: "All Device Types" },
          { id: "attachedTo", filterKey: "attachedTo", labelKey: "Attached to: All" },
          { id: "installedAt", filterKey: "installedAt", labelKey: "Installed At" },
          ...(isSuperAdmin
            ? [
                { id: "location", filterKey: "location", labelKey: "Locations" },
                { id: "organization", filterKey: "organization", labelKey: "Organizations" },
              ]
            : []),
          { id: "site", filterKey: "site", labelKey: "Sites" },
          { id: "sort", filterKey: "sort", labelKey: "Sort By:" },
        ];
    const searchWidget = visibleFilterWidgets.find((widget) => widget.filterKey === "search");
    const controlWidgets = visibleFilterWidgets.filter((widget) => widget.filterKey !== "search");
    const selectedReadingIndex = filteredReadings.findIndex(
      (reading) => String(reading.trackingDeviceId) === String(selectedReadingId),
    );
    const selectedReading =
      selectedReadingIndex >= 0 ? filteredReadings[selectedReadingIndex] : null;
    const selectedTrendKey = getSensorTrendDeviceKey(selectedReading);
    const selectedTrendRangeKey = selectedTrendKey
      ? getSensorTrendRangeKey(selectedTrendDuration)
      : "";
    const selectedTrendLoadingKey = selectedTrendKey
      ? `${selectedTrendKey}:${selectedTrendRangeKey}`
      : "";
    const detailInsertIndex =
      selectedReadingIndex >= 0
        ? Math.min(
            filteredReadings.length - 1,
            selectedReadingIndex + (cardColumnCount - 1 - (selectedReadingIndex % cardColumnCount)),
          )
        : -1;

    return (
      <section className="asset-live-section" key={section.id}>
        <div className="asset-section-label">{t(section?.props?.labelKey || "Live Data")}</div>
        <div className="asset-filter-bar">
          {searchWidget ? renderLiveFilterWidget(searchWidget) : null}
          <div className="asset-filter-controls">
            {controlWidgets.map(renderLiveFilterWidget)}
          </div>
        </div>

        {lastUpdatedAt && (
          <div className="asset-updated-text">
            {t("Updated")} {lastUpdatedAt.toLocaleTimeString()}
          </div>
        )}

        {loading && readings.length === 0 ? (
          <div className="asset-loading">
            <CircularProgress size={28} />
          </div>
        ) : (
          <div className="asset-card-grid">
            {filteredReadings.map((reading, index) => (
              <React.Fragment key={reading.trackingDeviceId}>
                <ReadingCard
                  reading={reading}
                  selected={String(selectedReadingId) === String(reading.trackingDeviceId)}
                  onClick={() =>
                    setSelectedReadingId((current) =>
                      String(current) === String(reading.trackingDeviceId)
                        ? null
                        : reading.trackingDeviceId,
                    )
                  }
                />
                {index === detailInsertIndex && selectedReadingIndex >= 0 && (
                  <DetailPanel
                    reading={selectedReading}
                    onClose={() => setSelectedReadingId(null)}
                    trendEntry={sensorTrendByDevice[selectedTrendKey]}
                    trendLoading={sensorTrendLoadingKey === selectedTrendLoadingKey}
                    trendError={sensorTrendErrors[selectedTrendKey]}
                    trendDuration={selectedTrendDuration}
                    onTrendDurationChange={setSelectedTrendDuration}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {!loading && filteredReadings.length === 0 && (
          <div className="asset-empty-state">
            {t(section?.props?.emptyMessageKey || "No live device readings found")}
          </div>
        )}
      </section>
    );
  };

  const renderMonitorSection = (section) => {
    switch (section.type) {
      case "liveSummaryStats":
        return renderMetricsSection(section);
      case "liveDeviceGrid":
        return renderLiveDataSection(section);
      case "analyticsGrid": {
        const analyticsWidgets = getVisibleLayoutWidgets(section, roleKey).filter(
          (widget) => widget.type === "analyticsCard",
        );
        if (useJsonLayout && analyticsWidgets.length === 0) return null;

        return (
          <section className="asset-analytics-section" key={section.id}>
            <DeviceAnalyticsSection
              devices={analyticsDevices}
              stops={stops}
              alerts={alerts}
              widgets={analyticsWidgets}
              organizationId={organizationId}
              organizationIds={analyticsOrganizationIds}
            />
          </section>
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
      <TitleBar />
      <NavMenu />
      <SubTitleNew
        title={pageTitle}
        path={`${selectedOrganizationName} > Devices > ${pageTitle}`}
        showDashboardControls={false}
      />
      <main className="asset-monitor-page">
        {!organizationId && !isSuperAdmin && (
          <Alert severity="warning" sx={{ mx: 2, mb: 1 }}>
            {t("Please select an organization to view live device readings.")}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
            {error}
          </Alert>
        )}

        {monitorSections.map(renderMonitorSection)}
      </main>
      <Footer />
    </>
  );
}
